import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import { reportAPI, categoryAPI } from '../services/api';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import { toast } from 'react-toastify';
import { FiPieChart, FiTrendingUp, FiTrendingDown, FiUsers, FiFilter, FiCalendar } from 'react-icons/fi';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { formatAmount } from '../utils/currency';
import { useTone } from '../contexts/ToneContext';
import './Pages.css';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const Reports = () => {
  const { t, toneEnabled } = useTone();
  const [reportType, setReportType] = useState('monthly');
  const [monthlyData, setMonthlyData] = useState(null);
  const [categoryData, setCategoryData] = useState([]);
  const [yearlyData, setYearlyData] = useState(null);
  const [vendorData, setVendorData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Category filter state
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('all');
  const [selectedTxnType, setSelectedTxnType] = useState('expense');

  // Date range filter
  const [dateFilter, setDateFilter] = useState('current_month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Carry forward toggle
  const [carryForward, setCarryForward] = useState(false);
  const [previousBalance, setPreviousBalance] = useState(0);

  const dateRange = useMemo(() => {
    const now = new Date();
    switch (dateFilter) {
      case 'current_month':
        return { start: startOfMonth(now), end: endOfMonth(now) };
      case 'last_month': {
        const lastMonth = subMonths(now, 1);
        return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
      }
      case 'last_3_months':
        return { start: startOfMonth(subMonths(now, 2)), end: endOfMonth(now) };
      case 'yearly':
        return { start: new Date(now.getFullYear(), 0, 1), end: new Date(now.getFullYear(), 11, 31) };
      case 'custom':
        return {
          start: customStart ? new Date(customStart) : null,
          end: customEnd ? new Date(customEnd) : null,
        };
      case 'all':
      default:
        return { start: null, end: null };
    }
  }, [dateFilter, customStart, customEnd]);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await categoryAPI.getAll();
        const cats = (res.data.data.categories || []).filter(c => !(c.is_system && c.name === 'Internal Transfer'));
        setCategories(cats);
      } catch (e) {
        // Silently fail - categories are optional for filtering
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchReports();
  }, [reportType, selectedCategoryId, selectedTxnType, dateFilter, customStart, customEnd]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      // Build date params
      const dateParams = {};
      if (dateRange.start) dateParams.start_date = format(dateRange.start, 'yyyy-MM-dd');
      if (dateRange.end) dateParams.end_date = format(dateRange.end, 'yyyy-MM-dd');

      if (reportType === 'monthly') {
        const response = await reportAPI.getMonthly(dateParams);
        setMonthlyData(response.data.data.summary);

        // Calculate previous balance for carry forward
        if (carryForward && dateRange.start) {
          try {
            const prevParams = {
              end_date: format(new Date(dateRange.start.getTime() - 86400000), 'yyyy-MM-dd'),
            };
            const prevRes = await reportAPI.getMonthly(prevParams);
            const prevSummary = prevRes.data.data.summary;
            setPreviousBalance(prevSummary ? parseFloat(prevSummary.balance || 0) : 0);
          } catch {
            setPreviousBalance(0);
          }
        } else {
          setPreviousBalance(0);
        }
      } else if (reportType === 'category') {
        const params = { transaction_type: selectedTxnType, ...dateParams };
        const response = await reportAPI.getCategory(params);
        setCategoryData(response.data.data.categories || []);
      } else if (reportType === 'yearly') {
        const response = await reportAPI.getYearly(dateParams);
        setYearlyData(response.data.data);
      } else if (reportType === 'vendor') {
        const response = await reportAPI.getVendor(dateParams);
        setVendorData(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const getPieData = () => {
    if (!categoryData.length) return null;

    let filteredData = categoryData.filter(c =>
      c.transaction_type === selectedTxnType ||
      c.transaction_type === `${selectedTxnType}_paid` ||
      c.transaction_type === `${selectedTxnType}_received`
    );

    if (selectedCategoryId !== 'all') {
      filteredData = filteredData.filter(c => c.category_id === selectedCategoryId);
    }

    if (!filteredData.length) return null;

    return {
      labels: filteredData.map(c => c.category_name || 'Uncategorized'),
      datasets: [{
        data: filteredData.map(c => parseFloat(c.total_amount)),
        backgroundColor: filteredData.map(c => c.category_color || '#95A5A6'),
      }],
    };
  };

  const getBarData = () => {
    if (!yearlyData) return null;
    const months = yearlyData.months || [];
    if (!months.length) return null;
    const year = yearlyData.year || new Date().getFullYear();
    return {
      labels: months.map((_, i) => new Date(year, i).toLocaleString('default', { month: 'short' })),
      datasets: [
        {
          label: t('reports.income_label'),
          data: months.map(m => m.income),
          backgroundColor: '#27AE60',
        },
        {
          label: t('reports.expense_label'),
          data: months.map(m => m.expense),
          backgroundColor: '#E74C3C',
        },
      ],
    };
  };

  const formatDateRange = () => {
    if (!dateRange.start || !dateRange.end) return 'All Time';
    return `${format(dateRange.start, 'dd MMM yyyy')} - ${format(dateRange.end, 'dd MMM yyyy')}`;
  };

  return (
    <Layout>
      <div className="page-container">
        <div className="page-header">
          <h1><FiPieChart style={{ marginRight: 8, verticalAlign: 'middle' }} />{t('reports.title')}</h1>
          <select value={reportType} onChange={(e) => setReportType(e.target.value)} className="select-input">
            <option value="monthly">{t('reports.monthly_summary')}</option>
            <option value="category">{t('reports.category_breakdown')}</option>
            <option value="yearly">{t('reports.yearly_overview')}</option>
            <option value="vendor">Vendor Summary</option>
          </select>
        </div>

        {/* Date Range Filter */}
        <div className="card reports-filter-bar">
          <div className="reports-filter-group">
            <FiCalendar size={16} style={{ color: 'var(--color-text-secondary)' }} />
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="select-input"
            >
              <option value="current_month">{toneEnabled ? 'Is Mahine' : 'Current Month'}</option>
              <option value="last_month">{toneEnabled ? 'Pichle Mahine' : 'Last Month'}</option>
              <option value="last_3_months">{toneEnabled ? 'Pichle 3 Mahine' : 'Last 3 Months'}</option>
              <option value="yearly">{toneEnabled ? 'Is Saal' : 'This Year'}</option>
              <option value="custom">{toneEnabled ? 'Apni Dates' : 'Custom Range'}</option>
              <option value="all">{toneEnabled ? 'Sab Dikhaao' : 'All Time'}</option>
            </select>
          </div>

          {dateFilter === 'custom' && (
            <div className="reports-filter-group">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="select-input"
              />
              <span style={{ color: 'var(--color-text-tertiary)' }}>to</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="select-input"
              />
            </div>
          )}

          {dateFilter !== 'all' && dateFilter !== 'custom' && (
            <span style={{ fontSize: '0.82rem', color: 'var(--color-text-tertiary)' }}>
              {formatDateRange()}
            </span>
          )}

          {/* Carry Forward Toggle — inline with filter bar */}
          {(reportType === 'monthly' || reportType === 'category') && dateFilter !== 'all' && (
            <div className="reports-carry-forward-inline">
              <label>
                <input
                  type="checkbox"
                  checked={carryForward}
                  onChange={(e) => setCarryForward(e.target.checked)}
                />
                {toneEnabled ? 'Pichhla balance jodo' : 'Carry Forward'}
              </label>
              {carryForward && previousBalance !== 0 && (
                <span className="reports-carry-note">
                  Opening: {formatAmount(previousBalance)}
                </span>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <LoadingSpinner message={t('common.loading')} />
        ) : (
          <>
            {reportType === 'monthly' && (
              monthlyData ? (
                <div className="card">
                  <h2>{t('reports.monthly_summary')}</h2>
                  <div className="summary-grid">
                    {carryForward && previousBalance !== 0 && (
                      <div className="summary-item">
                        <span>{toneEnabled ? 'Pichhla Balance' : 'Opening Balance'}:</span>
                        <strong style={{ color: previousBalance >= 0 ? 'var(--color-primary)' : 'var(--color-red)' }}>
                          {formatAmount(previousBalance)}
                        </strong>
                      </div>
                    )}
                    <div className="summary-item">
                      <span><FiTrendingUp style={{ marginRight: 4, verticalAlign: 'middle' }} />{t('reports.total_income')}:</span>
                      <strong className="text-success">{formatAmount(monthlyData.income)}</strong>
                    </div>
                    <div className="summary-item">
                      <span><FiTrendingDown style={{ marginRight: 4, verticalAlign: 'middle' }} />{t('reports.total_expense')}:</span>
                      <strong className="text-danger">{formatAmount(monthlyData.expense)}</strong>
                    </div>
                    <div className="summary-item">
                      <span>{t('reports.net_balance')}:</span>
                      <strong>
                        {formatAmount(
                          carryForward
                            ? (parseFloat(monthlyData.balance || 0) + previousBalance)
                            : monthlyData.balance
                        )}
                      </strong>
                    </div>
                  </div>
                </div>
              ) : (
                <EmptyState
                  icon={<FiPieChart />}
                  title={t('reports.no_monthly_data')}
                  message={t('reports.add_txn_monthly_msg')}
                />
              )
            )}

            {reportType === 'category' && (
              <>
                {/* Category Report Filters */}
                <div className="card reports-filter-bar">
                  <div className="reports-filter-group">
                    <FiFilter size={16} style={{ color: 'var(--color-text-secondary)' }} />
                    <span className="reports-filter-label">{toneEnabled ? 'Filter' : 'Filters'}:</span>
                  </div>
                  <div className="reports-filter-group">
                    <label className="reports-filter-label">{toneEnabled ? 'Tarika' : 'Type'}:</label>
                    <select
                      value={selectedTxnType}
                      onChange={(e) => setSelectedTxnType(e.target.value)}
                      className="select-input"
                    >
                      <option value="expense">{toneEnabled ? 'Kharcha' : 'Expense'}</option>
                      <option value="income">{toneEnabled ? 'Aaya' : 'Income'}</option>
                    </select>
                  </div>
                  <div className="reports-filter-group">
                    <label className="reports-filter-label">{toneEnabled ? 'Category' : 'Category'}:</label>
                    <select
                      value={selectedCategoryId}
                      onChange={(e) => setSelectedCategoryId(e.target.value)}
                      className="select-input"
                    >
                      <option value="all">{toneEnabled ? 'Sab Categories' : 'All Categories'}</option>
                      {categories
                        .filter(c => c.category_type === selectedTxnType)
                        .map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))
                      }
                    </select>
                  </div>
                </div>

                {/* Category Chart */}
                {getPieData() ? (
                  <div className="card">
                    <h2>
                      {selectedTxnType === 'expense'
                        ? (toneEnabled ? 'Kharche ka breakdown' : t('reports.expense_by_category'))
                        : (toneEnabled ? 'Aamdani ka breakdown' : 'Income by Category')
                      }
                    </h2>
                    <div className="chart-container">
                      <Pie data={getPieData()} />
                    </div>
                  </div>
                ) : (
                  <EmptyState
                    icon={<FiPieChart />}
                    title={t('reports.no_category_data')}
                    message={t('reports.add_categorized_msg')}
                  />
                )}
              </>
            )}

            {reportType === 'yearly' && (
              getBarData() ? (
                <div className="card">
                  <h2>{t('reports.yearly_overview')} ({yearlyData.year || new Date().getFullYear()})</h2>
                  <div className="chart-container">
                    <Bar data={getBarData()} options={{ responsive: true, maintainAspectRatio: true }} />
                  </div>
                </div>
              ) : (
                <EmptyState
                  icon={<FiPieChart />}
                  title={t('reports.no_yearly_data')}
                  message={t('reports.add_yearly_msg')}
                />
              )
            )}

            {reportType === 'vendor' && (
              vendorData && vendorData.vendors?.length > 0 ? (
                <div className="card">
                  <h2><FiUsers style={{ marginRight: 8, verticalAlign: 'middle' }} />
                    {toneEnabled ? 'Vendor Hisab' : 'Vendor Summary'}
                  </h2>

                  <div className="summary-grid" style={{ marginBottom: 24 }}>
                    <div className="summary-item">
                      <span>{toneEnabled ? 'Kul Paid' : 'Total Paid'}:</span>
                      <strong className="text-danger">{formatAmount(vendorData.summary?.total_paid || 0)}</strong>
                    </div>
                    <div className="summary-item">
                      <span style={{ color: 'var(--color-purple)' }}>{toneEnabled ? 'Kul Baaki' : 'Total Due'}:</span>
                      <strong style={{ color: 'var(--color-purple)' }}>{formatAmount(vendorData.summary?.total_due || 0)}</strong>
                    </div>
                    <div className="summary-item">
                      <span>{toneEnabled ? 'Net Balance' : 'Net Balance'}:</span>
                      <strong>{formatAmount(vendorData.summary?.net_balance || 0)}</strong>
                    </div>
                  </div>

                  <div className="table-container" style={{ boxShadow: 'none', padding: 0 }}>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>{toneEnabled ? 'Vendor' : 'Vendor'}</th>
                          <th style={{ textAlign: 'right' }}>{toneEnabled ? 'Diya' : 'Paid'}</th>
                          <th style={{ textAlign: 'right', color: 'var(--color-purple)' }}>{toneEnabled ? 'Baaki' : 'Due'}</th>
                          <th style={{ textAlign: 'right' }}>{toneEnabled ? 'Balance' : 'Balance'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vendorData.vendors.map((vendor) => (
                          <tr key={vendor.vendor_id}>
                            <td>
                              <strong>{vendor.vendor_name}</strong>
                              {vendor.vendor_phone && (
                                <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>{vendor.vendor_phone}</div>
                              )}
                            </td>
                            <td style={{ textAlign: 'right' }} className="text-danger">
                              {formatAmount(vendor.total_paid)}
                            </td>
                            <td style={{ textAlign: 'right', color: 'var(--color-purple)' }}>
                              {formatAmount(vendor.total_due)}
                            </td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>
                              {formatAmount(vendor.balance)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <EmptyState
                  icon={<FiUsers />}
                  title={toneEnabled ? 'Koi vendor data nahi' : 'No Vendor Data'}
                  message={toneEnabled ? 'Vendors aur transactions add karo vendor summary dekhne ke liye.' : 'Add vendors and transactions to see vendor summary.'}
                />
              )
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default Reports;
